import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  CircleAlert,
  Grid2X2,
  LayoutDashboard,
  LogOut,
  Menu,
  MonitorDot,
  Plug,
  Presentation,
  Search,
  Server,
  Settings,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useDashboardSummary, useHosts, useWorkspace } from "@/hooks/use-api";
import { useTimeAgo } from "@/hooks/use-time-ago";
import { APP_ROUTES, isAppPathActive } from "@/lib/app-routes";
import { cn } from "@/lib/utils";
import { LifecycleBanner } from "@/components/layout/lifecycle-banner";
import { QuotaBanner } from "@/components/layout/quota-banner";
import { ImpersonationBanner } from "@/components/superadmin/impersonation-banner";
import { useImpersonation } from "@/providers/impersonation-provider";

interface ProductionAppShellProps {
  children: ReactNode;
}

interface AppNavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  mspOnly?: boolean;
  superadminOnly?: boolean;
  end?: boolean;
}

interface AppNavGroup {
  label: string;
  items: AppNavItem[];
}

const NAV_GROUPS: AppNavGroup[] = [
  {
    label: "Monitor",
    items: [
      { label: "Dashboard", path: APP_ROUTES.dashboard, icon: LayoutDashboard, end: true },
      { label: "Alerts", path: APP_ROUTES.alerts, icon: Bell },
    ],
  },
  {
    label: "Investigate",
    items: [
      { label: "Investigation", path: APP_ROUTES.investigation, icon: Search },
      { label: "Logs", path: APP_ROUTES.logs, icon: MonitorDot },
    ],
  },
  {
    label: "Assets",
    items: [
      { label: "Integrations", path: APP_ROUTES.integrations, icon: Plug },
      { label: "Hosts", path: APP_ROUTES.hosts, icon: Server },
    ],
  },
  {
    label: "Report",
    items: [
      { label: "Executive", path: APP_ROUTES.executive, icon: Presentation, adminOnly: true },
    ],
  },
  {
    label: "Manage",
    items: [
      { label: "MSP Portal", path: APP_ROUTES.msp, icon: Grid2X2, mspOnly: true },
      { label: "Usage", path: APP_ROUTES.usage, icon: BarChart3, adminOnly: true },
      { label: "Settings", path: APP_ROUTES.settings, icon: Settings },
    ],
  },
  {
    label: "Superadmin",
    items: [
      { label: "Platform Admin", path: APP_ROUTES.superadmin, icon: ShieldCheck, superadminOnly: true, end: true },
      { label: "All Tenants", path: APP_ROUTES.superadminTenants, icon: Building2, superadminOnly: true },
      { label: "All Users", path: APP_ROUTES.superadminUsers, icon: Users, superadminOnly: true },
    ],
  },
];

const MOBILE_PRIMARY = [
  { label: "Home", path: APP_ROUTES.dashboard, icon: LayoutDashboard },
  { label: "Alerts", path: APP_ROUTES.alerts, icon: Bell },
  { label: "Investigate", path: APP_ROUTES.investigation, icon: Search },
] as const;

export function ProductionAppShell({ children }: ProductionAppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { targetTenantName } = useImpersonation();
  const summary = useDashboardSummary();
  const hosts = useHosts();
  const workspace = useWorkspace();
  const dataAge = useTimeAgo(hosts.dataUpdatedAt || summary.dataUpdatedAt);
  const [moreOpen, setMoreOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.roles?.some((role) => role === "admin" || role === "owner") ?? false;
  const isMsp = user?.roles?.includes("msp") ?? false;
  const isSuperadmin = user?.isSuperadmin ?? false;
  const visibleGroups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items
          .filter((item) => !item.adminOnly || isAdmin)
          .filter((item) => !item.mspOnly || isMsp)
          .filter((item) => !item.superadminOnly || isSuperadmin),
      })).filter((group) => group.items.length > 0),
    [isAdmin, isMsp, isSuperadmin],
  );

  const currentItem = visibleGroups
    .flatMap((group) => group.items)
    .find((item) => isNavItemActive(location.pathname, item));
  const workspaceName = workspace.data?.name ?? targetTenantName ?? "Security workspace";
  const alertBadge =
    (summary.data?.open_alerts.critical ?? 0) +
    (summary.data?.open_alerts.high ?? 0);
  const attentionAgents =
    (hosts.data?.summary.stale ?? 0) + (hosts.data?.summary.dead ?? 0);
  const isAlertWorkspace = location.pathname.startsWith(APP_ROUTES.alerts);
  const dataStatusUnavailable =
    (summary.isError && !summary.data) || (hosts.isError && !hosts.data);
  const dataStatusLoading =
    !dataStatusUnavailable &&
    ((summary.isLoading && !summary.data) || (hosts.isLoading && !hosts.data));
  const dataStatusReady =
    !dataStatusUnavailable && !dataStatusLoading && Boolean(summary.data || hosts.data);

  useEffect(() => {
    setMoreOpen(false);
    setWorkspaceOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, []);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = searchValue.trim().toLowerCase();
    if (!query) return;

    const match = visibleGroups
      .flatMap((group) => group.items)
      .find((item) => item.label.toLowerCase().includes(query));

    if (match) {
      navigate(match.path);
    } else {
      navigate(`${APP_ROUTES.investigation}?q=${encodeURIComponent(searchValue.trim())}`);
    }
    setSearchValue("");
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--app-canvas))] text-foreground">
      <a
        href="#main-content"
        className="sr-only fixed left-4 top-4 z-[100] rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:not-sr-only"
      >
        Skip to main content
      </a>

      <DesktopSidebar
        groups={visibleGroups}
        pathname={location.pathname}
        workspaceName={workspaceName}
        workspaceOpen={workspaceOpen}
        onWorkspaceToggle={() => setWorkspaceOpen((open) => !open)}
        isMsp={isMsp}
        email={user?.email ?? "User"}
        alertBadge={alertBadge}
        onLogout={logout}
      />

      <div className="min-h-screen lg:pl-[224px]">
        <ImpersonationBanner />
        <LifecycleBanner />
        <QuotaBanner />
        <header className="sticky top-0 z-30 h-16 border-b border-border/80 bg-[hsl(var(--app-canvas)/0.95)] backdrop-blur">
          <div className="flex h-full items-center gap-3 px-4 lg:px-6">
            <div className="flex min-w-0 items-center gap-3 lg:hidden">
              <img src="/logo.png" alt="" aria-hidden className="h-8 w-8 object-contain" />
              <button
                type="button"
                className="flex min-w-0 items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium"
                onClick={() => setMoreOpen(true)}
                aria-label={`Current workspace: ${workspaceName}. Open menu`}
              >
                <span className="max-w-[145px] truncate">{workspaceName}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </div>

            <div className="hidden min-w-0 items-center gap-2 text-sm lg:flex">
              <span className="truncate text-muted-foreground">{workspaceName}</span>
              <span aria-hidden className="text-muted-foreground/50">/</span>
              <span className="truncate font-medium">{currentItem?.label ?? "Workspace"}</span>
            </div>

            <form
              className="mx-auto hidden w-full max-w-md md:block"
              role="search"
              onSubmit={handleSearch}
            >
              <label htmlFor="app-global-search" className="sr-only">
                Search pages, alerts, users, and IP addresses
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={searchRef}
                  id="app-global-search"
                  type="search"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Search alerts, users, IPs…"
                  className="h-10 w-full rounded-lg border border-input bg-card pl-9 pr-14 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                />
                <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  ⌘ K
                </kbd>
              </div>
            </form>

            <div className="ml-auto flex shrink-0 items-center gap-2 lg:gap-3">
              {(dataStatusReady || dataStatusLoading || dataStatusUnavailable) && (
                <div className="hidden items-center gap-2 text-xs text-muted-foreground xl:flex">
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      dataStatusReady && "bg-primary",
                      dataStatusLoading && "animate-pulse bg-muted-foreground",
                      dataStatusUnavailable && "bg-amber-400",
                    )}
                  />
                  {dataStatusReady
                    ? `Data current${dataAge ? ` · ${dataAge}` : ""}`
                    : dataStatusLoading
                      ? "Checking data status"
                      : "Data status unavailable"}
                </div>
              )}

              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                {initialsFromEmail(user?.email)}
              </div>
            </div>
          </div>
        </header>

        <main
          id="main-content"
          className={cn(
            "min-h-[calc(100dvh-4rem)]",
            isAlertWorkspace ? "p-0" : "p-4 pb-24 md:p-6 md:pb-24 lg:pb-6",
          )}
        >
          {children}
        </main>
      </div>

      <MobileBottomNavigation
        pathname={location.pathname}
        alertBadge={alertBadge}
        moreOpen={moreOpen}
        onOpenMore={() => setMoreOpen(true)}
      />

      <MobileMoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        workspaceName={workspaceName}
        groups={visibleGroups}
        pathname={location.pathname}
        attentionAgents={attentionAgents}
        email={user?.email ?? "User"}
        onLogout={logout}
      />
    </div>
  );
}

function DesktopSidebar({
  groups,
  pathname,
  workspaceName,
  workspaceOpen,
  onWorkspaceToggle,
  isMsp,
  email,
  alertBadge,
  onLogout,
}: {
  groups: AppNavGroup[];
  pathname: string;
  workspaceName: string;
  workspaceOpen: boolean;
  onWorkspaceToggle: () => void;
  isMsp: boolean;
  email: string;
  alertBadge: number;
  onLogout: () => void;
}) {
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-[224px] flex-col border-r border-border/80 bg-[hsl(var(--app-sidebar))] lg:flex">
      <div className="flex h-16 items-center gap-2 border-b border-border/80 px-4">
        <img src="/logo.png" alt="" aria-hidden className="h-8 w-8 object-contain" />
        <span className="text-base font-semibold tracking-tight">prayingmantis</span>
      </div>

      <div className="relative px-3 pt-3">
        <button
          type="button"
          onClick={onWorkspaceToggle}
          className="flex w-full items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-secondary"
          aria-expanded={workspaceOpen}
          aria-haspopup="menu"
        >
          <Building2 className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate">{workspaceName}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
        {workspaceOpen && (
          <div
            role="menu"
            aria-label="Workspace options"
            className="absolute left-3 right-3 top-[calc(100%+0.4rem)] z-50 rounded-lg border border-border bg-popover p-1.5 shadow-xl"
          >
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-secondary"
            >
              <Check className="h-4 w-4 text-primary" />
              <span className="flex-1 truncate">{workspaceName}</span>
              <span className="text-[10px] text-muted-foreground">Current</span>
            </button>
            {isMsp && (
              <Link
                to={APP_ROUTES.msp}
                role="menuitem"
                className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <Grid2X2 className="h-4 w-4" />
                Portfolio view
              </Link>
            )}
          </div>
        )}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-3" aria-label="Application navigation">
        {groups.map((group) => (
          <div key={group.label} className="mb-4">
            <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const active = isNavItemActive(pathname, item);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                    )}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.label === "Alerts" && alertBadge > 0 && (
                      <span className="min-w-5 rounded-full bg-red-500/80 px-1.5 py-0.5 text-center text-[10px] font-semibold text-white">
                        {alertBadge > 99 ? "99+" : alertBadge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-border/80 p-3">
        <a
          href="/docs"
          className="mb-2 flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <BookOpen className="h-[18px] w-[18px]" />
          Docs
        </a>
        <div className="flex items-center gap-2 rounded-lg bg-card px-2.5 py-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
            {initialsFromEmail(email)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">{displayNameFromEmail(email)}</p>
            <p className="truncate text-[10px] text-muted-foreground">{email}</p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function MobileBottomNavigation({
  pathname,
  alertBadge,
  moreOpen,
  onOpenMore,
}: {
  pathname: string;
  alertBadge: number;
  moreOpen: boolean;
  onOpenMore: () => void;
}) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-border bg-[hsl(var(--app-sidebar)/0.98)] pb-[env(safe-area-inset-bottom)] lg:hidden"
      aria-label="Primary mobile navigation"
    >
      {MOBILE_PRIMARY.map((item) => {
        const Icon = item.icon;
        const active = isAppPathActive(pathname, item.path);
        return (
          <Link
            key={item.path}
            to={item.path}
            aria-current={active ? "page" : undefined}
            className={cn(
              "relative flex min-h-[68px] flex-col items-center justify-center gap-1 text-[11px] font-medium",
              active ? "text-primary" : "text-muted-foreground",
            )}
          >
            <Icon className="h-5 w-5" />
            <span>{item.label}</span>
            {item.label === "Alerts" && alertBadge > 0 && (
              <span className="absolute left-1/2 top-2 ml-1 min-w-5 rounded-full bg-red-500 px-1 py-0.5 text-[9px] font-semibold leading-none text-white">
                {alertBadge > 99 ? "99+" : alertBadge}
              </span>
            )}
          </Link>
        );
      })}
      <button
        type="button"
        onClick={onOpenMore}
        aria-expanded={moreOpen}
        className={cn(
          "flex min-h-[68px] flex-col items-center justify-center gap-1 text-[11px] font-medium",
          moreOpen || isMorePath(pathname) ? "text-primary" : "text-muted-foreground",
        )}
      >
        <Menu className="h-5 w-5" />
        <span>More</span>
      </button>
    </nav>
  );
}

function MobileMoreSheet({
  open,
  onClose,
  workspaceName,
  groups,
  pathname,
  attentionAgents,
  email,
  onLogout,
}: {
  open: boolean;
  onClose: () => void;
  workspaceName: string;
  groups: AppNavGroup[];
  pathname: string;
  attentionAgents: number;
  email: string;
  onLogout: () => void;
}) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !sheetRef.current) return;
      const focusable = Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const sheetGroups = groups
    .filter((group) => group.label !== "Monitor")
    .map((group) => ({
      ...group,
      items:
        group.label === "Investigate"
          ? group.items.filter((item) => item.path !== APP_ROUTES.investigation)
          : group.items,
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close More menu"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-more-title"
        className="absolute inset-x-0 bottom-0 max-h-[86dvh] overflow-y-auto rounded-t-3xl border-t border-border bg-[hsl(var(--app-sheet))] px-4 pb-[calc(5rem+env(safe-area-inset-bottom))] pt-3 shadow-2xl"
      >
        <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-muted-foreground/35" />
        <div className="mb-4 flex items-center gap-3">
          <div>
            <p className="text-xs font-medium text-primary">{workspaceName}</p>
            <h2 id="app-more-title" className="text-2xl font-semibold">More</h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="ml-auto flex h-11 w-11 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground"
            aria-label="Close More menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <Link
          to={APP_ROUTES.hosts}
          onClick={onClose}
          className="mb-5 flex min-h-12 items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5"
        >
          <span className={cn("h-2.5 w-2.5 rounded-full", attentionAgents > 0 ? "bg-amber-400" : "bg-primary")} />
          <span className="flex-1 text-sm font-medium">
            {attentionAgents > 0
              ? `${attentionAgents} agent${attentionAgents === 1 ? "" : "s"} need attention`
              : "All agents current"}
          </span>
          <CircleAlert className="h-4 w-4 text-muted-foreground" />
        </Link>

        {sheetGroups.map((group) => (
          <div key={group.label} className="mb-5">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {group.label === "Assets" ? "Data" : group.label === "Report" ? "Reporting" : group.label}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isNavItemActive(pathname, item);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex min-h-[72px] items-center gap-3 rounded-xl border px-3 text-sm font-medium",
                      active
                        ? "border-primary/40 bg-primary/10 text-primary"
                        : "border-border bg-card text-foreground",
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mb-4">
          <a
            href="/docs"
            className="flex min-h-12 items-center gap-3 rounded-xl border border-border bg-card px-3 text-sm font-medium"
          >
            <BookOpen className="h-5 w-5 text-muted-foreground" />
            Docs
          </a>
        </div>

        <div className="flex items-center gap-3 border-t border-border pt-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold">
            {initialsFromEmail(email)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{displayNameFromEmail(email)}</p>
            <p className="truncate text-xs text-muted-foreground">{email}</p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-medium text-primary"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function isNavItemActive(pathname: string, item: AppNavItem): boolean {
  return item.end
    ? pathname === item.path || pathname === `${item.path}/`
    : isAppPathActive(pathname, item.path);
}

function isMorePath(pathname: string): boolean {
  return [
    APP_ROUTES.integrations,
    APP_ROUTES.hosts,
    APP_ROUTES.executive,
    APP_ROUTES.usage,
    APP_ROUTES.settings,
    APP_ROUTES.msp,
    APP_ROUTES.logs,
    APP_ROUTES.superadmin,
  ].some((path) => pathname.startsWith(path));
}

function initialsFromEmail(email?: string): string {
  if (!email) return "U";
  return email.charAt(0).toUpperCase();
}

function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0] || "User";
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
